import { useCallback, useState } from "react";
import { Lightning } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { useAppDispatch } from "state/hooks.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { fetchOrders } from "state/slices/user-orders-slice.ts";
import { setWalletIsStale } from "state/slices/wallet-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { reportError } from "utils/report-error";
import { DepositFundsRequest } from "interfaces/commerce/order/create-order-request.ts";
import { Order } from "interfaces/commerce/order/order.ts";
import { getOrdersPath } from "utils/backend-path-builders.ts";
import { postSpeedPayInvoiceMessage } from "utils/integration/iframe-messages.ts";
import { getSpeedSession } from "utils/integration/integrated-app-tools.ts";
import GreyModalSection from "../grey-modal-section.tsx";
import AmountSelector from "../amount-selector.tsx";
import StyledInteractiveButton from "./styled-interactive-button.tsx";

interface SpeedPaymentBlockProps {
  onClose: () => void;
}

const PRESET_AMOUNTS = [
  { amount: 5 },
  { amount: 20 },
  { amount: 100 },
];

/**
 * SpeedPaymentBlock renders the Speed-Wallet-specific deposit flow, used as a
 * drop-in replacement for CryptoPaymentBlock (GLIDE) when Sparket is running
 * inside the Speed Wallet Mini App.
 *
 * Flow:
 *   1. User picks an amount.
 *   2. Tapping "Deposit via SpeedWallet" calls POST /v2/order with
 *      paymentMethodType=SPEED_MERCHANT. The backend creates a Speed payment
 *      and returns the BOLT11 invoice in order.payment_page_url.
 *   3. We postMessage the invoice to the Speed Wallet host, which opens its
 *      native "Pay invoice" sheet.
 *   4. After the user pays, Speed delivers `payment.confirmed` to our webhook,
 *      which credits the wallet. We refresh the wallet/orders a few times.
 */
const SpeedPaymentBlock = ({ onClose }: SpeedPaymentBlockProps) => {
  const dispatch = useAppDispatch();
  const [amount, setAmount] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const refreshBalances = useCallback(() => {
    dispatch(fetchOrders());
    dispatch(fetchRealWallet());
    dispatch(setWalletIsStale(true));
  }, [dispatch]);

  const handleDeposit = useCallback(async () => {
    if (!amount || amount <= 0) {
      toast.error("Please enter a deposit amount.");
      return;
    }

    setLoading(true);

    try {
      const request: DepositFundsRequest = {
        amount,
        orderType: "DEPOSIT_FUNDS",
        successPathname: window.location.pathname,
        paymentMethodType: "SPEED_MERCHANT",
      };

      const order = (await postRequest(getOrdersPath(true), { body: request })) as Order;

      const invoice = order.payment_page_url;
      if (!invoice) {
        toast.error("Failed to create Lightning invoice. Please try again.");
        setLoading(false);
        return;
      }

      // `acct` is captured into sessionStorage by `useSpeedSessionHandshake`
      // at the App level whenever it appears in the URL (on any route). At
      // click time we just read it back — same pattern as
      // integrated-app-http-client reads USER_ID/TOKEN.
      const { accountId } = getSpeedSession();
      if (!accountId) {
        // eslint-disable-next-line no-console
        console.error(
          "[speed] missing accountId. url=%s sessionStorage keys=%o",
          window.location.href,
          Object.keys(window.sessionStorage || {})
        );
        toast.error(
          "Speed Wallet session expired. Please reopen the Mini App from Speed."
        );
        setLoading(false);
        return;
      }

      // Hand the BOLT11 invoice off to the Speed Wallet host. The host opens
      // its own "Pay invoice" UI, so from here our job is to refresh balances
      // when the webhook credits the deposit.
      postSpeedPayInvoiceMessage({
        accountId,
        amount,
        currency: "USD",
        targetCurrency: "SATS",
        depositAddress: invoice,
        note: `Sparket deposit ${order.id}`,
      });

      toast.info("Opening Speed Wallet to pay the invoice…");

      // Poll a few times — payment.confirmed typically arrives within seconds.
      refreshBalances();
      setTimeout(refreshBalances, 3000);
      setTimeout(refreshBalances, 8000);

      onClose();
    } catch (error) {
      reportError(error, "deposit", { method: "speed_lightning" });
      toast.error("Failed to create deposit. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [amount, onClose, refreshBalances]);

  return (
    <>
      <GreyModalSection>
        <AmountSelector
          presetAmounts={PRESET_AMOUNTS}
          selectedAmount={amount}
          setSelectedAmount={setAmount}
          placeholder="$ other amount"
          disabled={loading}
        />
      </GreyModalSection>

      <GreyModalSection>
        <div style={{ padding: "8px 4px" }}>
          <div
            style={{
              padding: "3px",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              marginBottom: "12px",
            }}
          >
            <div style={{ width: "100%" }}>
              <StyledInteractiveButton
                onClick={handleDeposit}
                disabled={loading || !amount}
                isActive={!!amount}
                variant="expandable"
              >
                <Lightning size={16} />
                {loading ? "Creating invoice…" : "Deposit via SpeedWallet"}
              </StyledInteractiveButton>
            </div>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.6)",
              textAlign: "center",
            }}
          >
            Pay a Lightning invoice from your Speed Wallet balance • Credited to USD balance
          </div>
        </div>
      </GreyModalSection>
    </>
  );
};

export default SpeedPaymentBlock;
